import React, { useState } from 'react';
import { 
  Code, 
  Book, 
  Key, 
  Lock, 
  CheckCircle, 
  AlertCircle,
  Copy,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import ApiKeysModal from './ApiKeysModal';
import { useAuth } from '../contexts/AuthContext';

const ApiDocsPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [activeSection, setActiveSection] = useState<string>('intro');
  const [copiedCode, setCopiedCode] = useState<string>('');
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set(['get-posts']));
  const [showApiKeysModal, setShowApiKeysModal] = useState(false);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const toggleEndpoint = (id: string) => {
    const newSet = new Set(expandedEndpoints);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedEndpoints(newSet);
  };

  const sections = [
    { id: 'intro', title: '소개', icon: Book },
    { id: 'apikeys', title: 'API 키 발급', icon: Key },
    { id: 'auth', title: '인증', icon: Lock },
    { id: 'posts', title: '게시글 API', icon: Code },
    { id: 'users', title: '사용자 API', icon: Code },
    { id: 'errors', title: '에러 코드', icon: AlertCircle },
  ];

  const baseUrl = 'https://pulse.ai.kr/api';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 mb-8 text-white">
          <h1 className="text-4xl font-bold mb-4">Pulse API 문서</h1>
          <p className="text-lg text-blue-100">의료 전문가를 위한 SNS 플랫폼 API</p>
          <div className="mt-4 flex items-center space-x-4">
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">v1.0.0</span>
            <span className="px-3 py-1 bg-green-500 rounded-full text-sm">Stable</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-4">
              <h3 className="font-semibold text-gray-900 mb-4">목차</h3>
              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {section.title}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-8">
              {/* 소개 */}
              {activeSection === 'intro' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">API 소개</h2>
                  
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed">
                      Pulse API는 의료 전문가들이 지식을 공유하고 협업할 수 있도록 설계된 RESTful API입니다.
                      이 API를 통해 게시글 조회, 사용자 인증, 댓글 관리 등 다양한 기능을 프로그래밍 방식으로 사용할 수 있습니다.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">주요 기능</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                        <span>게시글 CRUD (생성, 조회, 수정, 삭제)</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                        <span>사용자 인증 및 프로필 관리</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                        <span>댓글 및 좋아요 기능</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                        <span>AI 기반 의료 상담</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                        <span>실시간 알림</span>
                      </li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Base URL</h3>
                    <div className="bg-gray-900 rounded-lg p-4 relative">
                      <code className="text-green-400 font-mono">{baseUrl}</code>
                      <button
                        onClick={() => copyToClipboard(baseUrl, 'base-url')}
                        className="absolute right-4 top-4 text-gray-400 hover:text-white"
                      >
                        {copiedCode === 'base-url' ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">데이터 형식</h3>
                    <p className="text-gray-700">
                      모든 API 응답은 JSON 형식으로 제공됩니다. 요청 시 <code className="bg-gray-100 px-2 py-1 rounded">Content-Type: application/json</code> 헤더를 포함해야 합니다.
                    </p>
                  </div>
                </div>
              )}

              {/* API 키 발급 */}
              {activeSection === 'apikeys' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">API 키 발급</h2>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
                    <div className="flex items-center mb-4">
                      <Key className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-xl font-semibold text-gray-900">API 키 관리</h3>
                    </div>
                    
                    <p className="text-gray-700 mb-4">
                      Pulse API를 사용하기 위해서는 API 키가 필요합니다. 
                      키를 발급받아 HTTP 요청의 Authorization 헤더에 포함하여 사용하세요.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start space-x-2 text-sm text-gray-700">
                        <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <span>일일 요청 한도: <strong>10,000회</strong></span>
                      </div>
                      <div className="flex items-start space-x-2 text-sm text-gray-700">
                        <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <span>발급된 키는 <strong>한 번만</strong> 표시되므로 안전하게 보관하세요</span>
                      </div>
                      <div className="flex items-start space-x-2 text-sm text-gray-700">
                        <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <span>사용하지 않는 키는 즉시 철회하여 보안을 유지하세요</span>
                      </div>
                    </div>

                    {isAuthenticated ? (
                      <button
                        onClick={() => setShowApiKeysModal(true)}
                        className="flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                      >
                        <Key className="w-5 h-5 mr-2" />
                        API 키 발급 및 관리
                      </button>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                          <div>
                            <p className="font-semibold text-yellow-800">로그인이 필요합니다</p>
                            <p className="text-sm text-yellow-700 mt-1">
                              API 키를 발급받으려면 먼저 로그인하세요.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="prose max-w-none">
                    <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">API 키 사용 방법</h3>
                    <p className="text-gray-700 mb-4">
                      발급받은 API 키를 HTTP 요청의 <code className="bg-gray-100 px-2 py-1 rounded">Authorization</code> 헤더에 포함하여 사용합니다.
                    </p>

                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">요청 예시</h4>
                    <div className="bg-gray-900 rounded-lg p-4 relative">
                      <pre className="text-green-400 font-mono text-sm overflow-x-auto">
{`# cURL 예시
curl -H "Authorization: Bearer YOUR_API_KEY" \\
     ${baseUrl}/posts

# JavaScript (Fetch) 예시
fetch('${baseUrl}/posts', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
})
  .then(res => res.json())
  .then(data => console.log(data));

# Python (requests) 예시
import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY'
}
response = requests.get('${baseUrl}/posts', headers=headers)
data = response.json()`}
                      </pre>
                      <button
                        onClick={() => copyToClipboard(`curl -H "Authorization: Bearer YOUR_API_KEY" ${baseUrl}/posts`, 'api-key-usage')}
                        className="absolute right-4 top-4 text-gray-400 hover:text-white"
                      >
                        {copiedCode === 'api-key-usage' ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">보안 주의사항</h3>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="space-y-2 text-sm text-red-800">
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>API 키를 공개 저장소(GitHub 등)에 업로드하지 마세요</span>
                        </div>
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>환경 변수를 사용하여 키를 안전하게 관리하세요</span>
                        </div>
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>키가 노출된 경우 즉시 철회하고 새 키를 발급받으세요</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 인증 */}
              {activeSection === 'auth' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">인증</h2>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Key className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                      <div>
                        <p className="font-semibold text-blue-900">Bearer Token 인증</p>
                        <p className="text-sm text-blue-700 mt-1">
                          대부분의 API는 JWT 토큰을 사용한 인증이 필요합니다.
                        </p>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-3">로그인</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-mono text-sm mr-2">POST</span>
                        <code className="text-gray-700">/auth/login</code>
                      </div>
                    </div>
                  </div>

                  <h4 className="font-semibold text-gray-900 mt-4 mb-2">요청 본문</h4>
                  <div className="bg-gray-900 rounded-lg p-4 relative">
                    <pre className="text-green-400 font-mono text-sm overflow-x-auto">
{`{
  "email": "doctor@hospital.com",
  "password": "your_password"
}`}
                    </pre>
                    <button
                      onClick={() => copyToClipboard('{\n  "email": "doctor@hospital.com",\n  "password": "your_password"\n}', 'login-request')}
                      className="absolute right-4 top-4 text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'login-request' ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <h4 className="font-semibold text-gray-900 mt-4 mb-2">응답</h4>
                  <div className="bg-gray-900 rounded-lg p-4 relative">
                    <pre className="text-green-400 font-mono text-sm overflow-x-auto">
{`{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "user": {
    "id": 1,
    "email": "doctor@hospital.com",
    "name": "김의사",
    "hospital": "서울대학교병원",
    "specialization": "내과",
    "isVerifiedDoctor": true
  }
}`}
                    </pre>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">인증된 요청</h3>
                  <p className="text-gray-700 mb-4">
                    로그인 후 받은 토큰을 사용하여 인증이 필요한 엔드포인트에 접근할 수 있습니다.
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <pre className="text-green-400 font-mono text-sm">
{`Authorization: Bearer YOUR_JWT_TOKEN`}
                    </pre>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">회원가입</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-mono text-sm mr-2">POST</span>
                        <code className="text-gray-700">/auth/register</code>
                      </div>
                    </div>
                  </div>

                  <h4 className="font-semibold text-gray-900 mt-4 mb-2">요청 본문</h4>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <pre className="text-green-400 font-mono text-sm overflow-x-auto">
{`{
  "email": "newdoctor@hospital.com",
  "password": "secure_password",
  "name": "이의사",
  "hospital": "서울대학교병원",
  "specialization": "외과"
}`}
                    </pre>
                  </div>
                </div>
              )}

              {/* 게시글 API */}
              {activeSection === 'posts' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">게시글 API</h2>

                  {/* GET /posts */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleEndpoint('get-posts')}
                      className="w-full bg-gray-50 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-mono text-sm mr-3">GET</span>
                        <code className="text-gray-900 font-semibold">/guest/posts</code>
                        <span className="ml-3 text-sm text-gray-600">게시글 목록 조회</span>
                      </div>
                      {expandedEndpoints.has('get-posts') ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedEndpoints.has('get-posts') && (
                      <div className="p-4 space-y-4">
                        <p className="text-gray-700">게스트 사용자도 접근 가능한 공개 게시글 목록을 조회합니다.</p>
                        
                        <h4 className="font-semibold text-gray-900">쿼리 파라미터</h4>
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold">파라미터</th>
                              <th className="text-left px-3 py-2 font-semibold">타입</th>
                              <th className="text-left px-3 py-2 font-semibold">설명</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            <tr>
                              <td className="px-3 py-2 font-mono text-xs">page</td>
                              <td className="px-3 py-2 text-gray-600">integer</td>
                              <td className="px-3 py-2 text-gray-700">페이지 번호 (기본값: 1)</td>
                            </tr>
                            <tr>
                              <td className="px-3 py-2 font-mono text-xs">limit</td>
                              <td className="px-3 py-2 text-gray-600">integer</td>
                              <td className="px-3 py-2 text-gray-700">페이지당 게시글 수 (기본값: 10)</td>
                            </tr>
                            <tr>
                              <td className="px-3 py-2 font-mono text-xs">type</td>
                              <td className="px-3 py-2 text-gray-600">string</td>
                              <td className="px-3 py-2 text-gray-700">게시글 타입 필터 (case_study, research, discussion, job, medical_dispute)</td>
                            </tr>
                          </tbody>
                        </table>

                        <h4 className="font-semibold text-gray-900 mt-4">예시 요청</h4>
                        <div className="bg-gray-900 rounded-lg p-4 relative">
                          <pre className="text-green-400 font-mono text-sm">
{`GET ${baseUrl}/guest/posts?page=1&limit=10&type=case_study`}
                          </pre>
                          <button
                            onClick={() => copyToClipboard(`GET ${baseUrl}/guest/posts?page=1&limit=10&type=case_study`, 'get-posts-req')}
                            className="absolute right-4 top-4 text-gray-400 hover:text-white"
                          >
                            {copiedCode === 'get-posts-req' ? (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : (
                              <Copy className="w-5 h-5" />
                            )}
                          </button>
                        </div>

                        <h4 className="font-semibold text-gray-900 mt-4">응답</h4>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <pre className="text-green-400 font-mono text-xs overflow-x-auto">
{`{
  "posts": [
    {
      "id": 1,
      "title": "당뇨병 환자의 혈당 관리 사례",
      "content": "55세 남성 환자의 혈당 관리 사례를 공유합니다...",
      "type": "case_study",
      "authorId": 5,
      "authorName": "김의사",
      "hospital": "서울대학교병원",
      "specialization": "내과",
      "isVerifiedDoctor": true,
      "likes": 12,
      "views": 245,
      "commentsCount": 8,
      "images": ["https://example.com/image1.jpg"],
      "createdAt": "2025-10-15T10:30:00Z",
      "updatedAt": "2025-10-15T10:30:00Z"
    }
  ],
  "total": 152,
  "page": 1,
  "limit": 10,
  "hasMore": true
}`}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* POST /posts */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleEndpoint('create-post')}
                      className="w-full bg-gray-50 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-mono text-sm mr-3">POST</span>
                        <code className="text-gray-900 font-semibold">/posts</code>
                        <span className="ml-3 text-sm text-gray-600">게시글 작성</span>
                        <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">인증 필요</span>
                      </div>
                      {expandedEndpoints.has('create-post') ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedEndpoints.has('create-post') && (
                      <div className="p-4 space-y-4">
                        <p className="text-gray-700">새로운 게시글을 작성합니다. (인증 필요)</p>
                        
                        <h4 className="font-semibold text-gray-900">요청 본문</h4>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <pre className="text-green-400 font-mono text-xs overflow-x-auto">
{`{
  "title": "고혈압 치료 프로토콜 업데이트",
  "content": "최신 고혈압 치료 가이드라인에 대한 내용입니다...",
  "type": "research",
  "tags": ["고혈압", "치료", "가이드라인"]
}`}
                          </pre>
                        </div>

                        <h4 className="font-semibold text-gray-900 mt-4">응답</h4>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <pre className="text-green-400 font-mono text-xs overflow-x-auto">
{`{
  "message": "게시글이 작성되었습니다.",
  "postId": 153
}`}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* GET /posts/:id */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleEndpoint('get-post')}
                      className="w-full bg-gray-50 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-mono text-sm mr-3">GET</span>
                        <code className="text-gray-900 font-semibold">/posts/:id</code>
                        <span className="ml-3 text-sm text-gray-600">게시글 상세 조회</span>
                      </div>
                      {expandedEndpoints.has('get-post') ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedEndpoints.has('get-post') && (
                      <div className="p-4 space-y-4">
                        <p className="text-gray-700">특정 게시글의 상세 정보를 조회합니다.</p>
                        
                        <h4 className="font-semibold text-gray-900">예시 요청</h4>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <pre className="text-green-400 font-mono text-sm">
{`GET ${baseUrl}/guest/posts/1`}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 사용자 API */}
              {activeSection === 'users' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">사용자 API</h2>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleEndpoint('get-profile')}
                      className="w-full bg-gray-50 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-mono text-sm mr-3">GET</span>
                        <code className="text-gray-900 font-semibold">/users/:id</code>
                        <span className="ml-3 text-sm text-gray-600">프로필 조회</span>
                      </div>
                      {expandedEndpoints.has('get-profile') ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedEndpoints.has('get-profile') && (
                      <div className="p-4 space-y-4">
                        <p className="text-gray-700">사용자의 프로필 정보를 조회합니다.</p>
                        
                        <h4 className="font-semibold text-gray-900">응답</h4>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <pre className="text-green-400 font-mono text-xs overflow-x-auto">
{`{
  "id": 1,
  "name": "김의사",
  "email": "doctor@hospital.com",
  "hospital": "서울대학교병원",
  "specialization": "내과",
  "isVerifiedDoctor": true,
  "createdAt": "2025-09-15T08:00:00Z"
}`}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 에러 코드 */}
              {activeSection === 'errors' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">에러 코드</h2>
                  
                  <p className="text-gray-700">API는 표준 HTTP 상태 코드를 사용하여 요청의 성공 또는 실패를 나타냅니다.</p>

                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded font-mono text-sm mr-3">200</span>
                        <span className="font-semibold text-gray-900">OK</span>
                      </div>
                      <p className="text-gray-600 text-sm">요청이 성공적으로 처리되었습니다.</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded font-mono text-sm mr-3">201</span>
                        <span className="font-semibold text-gray-900">Created</span>
                      </div>
                      <p className="text-gray-600 text-sm">리소스가 성공적으로 생성되었습니다.</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-mono text-sm mr-3">400</span>
                        <span className="font-semibold text-gray-900">Bad Request</span>
                      </div>
                      <p className="text-gray-600 text-sm">잘못된 요청입니다. 요청 파라미터를 확인하세요.</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-mono text-sm mr-3">401</span>
                        <span className="font-semibold text-gray-900">Unauthorized</span>
                      </div>
                      <p className="text-gray-600 text-sm">인증이 필요합니다. 토큰을 확인하세요.</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-mono text-sm mr-3">403</span>
                        <span className="font-semibold text-gray-900">Forbidden</span>
                      </div>
                      <p className="text-gray-600 text-sm">접근 권한이 없습니다.</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-mono text-sm mr-3">404</span>
                        <span className="font-semibold text-gray-900">Not Found</span>
                      </div>
                      <p className="text-gray-600 text-sm">요청한 리소스를 찾을 수 없습니다.</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-mono text-sm mr-3">429</span>
                        <span className="font-semibold text-gray-900">Too Many Requests</span>
                      </div>
                      <p className="text-gray-600 text-sm">요청 한도를 초과했습니다. 잠시 후 다시 시도하세요.</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-mono text-sm mr-3">500</span>
                        <span className="font-semibold text-gray-900">Internal Server Error</span>
                      </div>
                      <p className="text-gray-600 text-sm">서버 오류가 발생했습니다. 관리자에게 문의하세요.</p>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">에러 응답 형식</h3>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <pre className="text-green-400 font-mono text-sm overflow-x-auto">
{`{
  "error": "에러 메시지",
  "code": "ERROR_CODE",
  "details": {
    "field": "구체적인 오류 정보"
  }
}`}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* API Keys Modal */}
      <ApiKeysModal 
        open={showApiKeysModal} 
        onClose={() => setShowApiKeysModal(false)} 
      />
    </div>
  );
};

export default ApiDocsPage;

